import type {
  EdgeId,
  NodeId,
  NodeSn,
  TerminatedPacketData,
} from '@ipfabric/utils';
import type { PathLookupPathInspectorSelectedPacketSource } from 'components/DeviceView/deviceViewTypes';
import { isPathLookupGraphMetadata } from 'components/Graph/GraphState';
import { unhighlightEdges } from 'components/Graph/commands/pathLookup/unhighlightEdges';
import { isPathLookupNodeData } from 'components/Graph/helpers/isPathLookupNodeData';
import type { GraphCommandFactory } from 'components/Graph/model/GraphCommandContext';
import type { GraphData } from 'components/Graph/model/GraphData';
import { array } from 'fp-ts';
import { findNodeIdByNodeSn } from './helpers/findNodeIdByNodeSn';
import { getPacketEdgeDetail } from './helpers/getPacketEdgeDetail';
import { getTerminatedPacketDetail } from './helpers/getTerminatedPacketDetail';

export interface SelectPathInspectorTerminatedPacketCommand {
  nodeSn: NodeSn;
  packetId: EdgeId;
  packetSource: PathLookupPathInspectorSelectedPacketSource;
}

const getPacketDetail = (
  packetSource: PathLookupPathInspectorSelectedPacketSource,
  packetId: EdgeId,
  graphData: GraphData,
  nodeId: NodeId,
) => {
  if (packetSource === 'edge') {
    return getPacketEdgeDetail(graphData, packetId);
  }
  const nodeData = graphData.nodes[nodeId];
  if (
    packetSource === 'generatedPackets' ||
    nodeData === undefined ||
    !isPathLookupNodeData(nodeData)
  ) {
    return null;
  }
  const foundPacketData: TerminatedPacketData | undefined =
    nodeData[packetSource]?.[packetId];
  if (foundPacketData === undefined) {
    return null;
  }
  return getTerminatedPacketDetail(packetId, foundPacketData);
};

export const selectPathInspectorTerminatedPacket: GraphCommandFactory<
  SelectPathInspectorTerminatedPacketCommand
> = (cmdContext, dispatch) => (command) => {
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
  cmdContext.updateState((draft) => {
    const nodeSelectedPackets =
      draft.pathlookup.pathInspector.selectedPackets[command.nodeSn];
    const nodeHighlightedEdges =
      draft.pathlookup.pathInspector.highlightedEdges[command.nodeSn];
    const nodeId = findNodeIdByNodeSn(draft.graph, command.nodeSn);
    if (nodeId === null || nodeSelectedPackets === undefined) {
      return;
    }

    const packetDetail = getPacketDetail(
      command.packetSource,
      command.packetId,
      draft.graph,
      nodeId,
    );
    if (packetDetail === null) {
      return;
    }

    if (nodeSelectedPackets.selectedIncomingPacket === null) {
      nodeSelectedPackets.selectedIncomingPacket = packetDetail;
    } else {
      nodeSelectedPackets.selectedOutgoingPacket = packetDetail;
      nodeSelectedPackets.selectedOutgoingPacketSource = command.packetSource;
    }

    if (nodeHighlightedEdges !== undefined) {
      nodeHighlightedEdges.outgoingEdges = [];
    }

    const decision =
      isPathLookupGraphMetadata(draft.graphMetadata) &&
      draft.graphMetadata.decisions[nodeId];
    if (!decision) {
      return;
    }
    const outgoingEdges = array.flatten(Object.values(decision.trafficOut));
    dispatch(unhighlightEdges, {
      edges: outgoingEdges,
      color: true,
    });
  });
};
